import { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { useT } from '../i18n';

// Subsequence-or-substring fuzzy match.
function fuzzy(query: string, text: string): boolean {
  const q = query.trim().toLowerCase();
  const t = (text || '').toLowerCase();
  if (!q) return true;
  if (t.includes(q)) return true;
  let i = 0;
  for (const ch of t) { if (ch === q[i]) i++; if (i === q.length) return true; }
  return false;
}

// Left-aligned fuzzy multi-select filter — chips show active selections; type to
// search; click to toggle. App-designed (no native select).
export function MultiSelectFilter<T extends string | number>({
  label, options, selected, onChange, placeholder,
}: {
  label: string;
  options: { value: T; label: string; sub?: string }[];
  selected: T[];
  onChange: (vals: T[]) => void;
  placeholder?: string;
}) {
  const { t } = useT();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setQ(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const matches = q.trim() ? options.filter(o => fuzzy(q, o.label)) : options;
  const selectedOpts = options.filter(o => selected.includes(o.value));
  const toggle = (v: T) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);

  return (
    <div className="msf" ref={ref}>
      <span className="msf-label">{label}</span>
      <div className="msf-control" onClick={() => setOpen(true)}>
        {selectedOpts.map(o => (
          <span key={String(o.value)} className="msf-chip">
            {o.label}
            <button type="button" className="msf-chip-x" onClick={(e) => { e.stopPropagation(); toggle(o.value); }}><X size={10} /></button>
          </span>
        ))}
        <input
          className="msf-input"
          value={q}
          placeholder={selectedOpts.length === 0 ? (placeholder || t('filters.searchPlaceholder')) : ''}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
      </div>
      {open && matches.length > 0 && (
        <div className="autosuggest-dropdown msf-dropdown">
          {matches.map(o => (
            <div
              key={String(o.value)}
              className={`autosuggest-option${selected.includes(o.value) ? ' msf-on' : ''}`}
              onClick={() => toggle(o.value)}
            >
              <span className="msf-check">{selected.includes(o.value) ? '✓' : ''}</span>
              {o.label}{o.sub ? <span className="text-ink-3"> · {o.sub}</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
